import React, { useMemo, useState } from 'react';
import PersonContactHover from '../../components/PersonContactHover';
import { isDeviceRepUserType, sortRows } from '../../utils/userAnalytics';
import SortableTh from './SortableTh';

function DeviceRepsTab({ profiles, yearLabel }) {
  const [search, setSearch] = useState('');
  const [customTypeFilter, setCustomTypeFilter] = useState('');
  const [sortKey, setSortKey] = useState('logins');
  const [sortDir, setSortDir] = useState('desc');

  const reps = useMemo(() => {
    const typeFilter = customTypeFilter.trim().toLowerCase();
    return profiles.filter(p => {
      if (p.logins <= 0) return false;
      if (typeFilter) return (p.userType || '').toLowerCase().includes(typeFilter);
      return isDeviceRepUserType(p.userType);
    });
  }, [profiles, customTypeFilter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return reps;
    return reps.filter(p =>
      p.name.toLowerCase().includes(q)
      || p.homeFacility.toLowerCase().includes(q)
      || (p.userType || '').toLowerCase().includes(q)
      || p.collaboratingFacilities.some(f => f.toLowerCase().includes(q))
    );
  }, [reps, search]);

  const sorted = useMemo(
    () => sortRows(filtered, sortKey, sortDir),
    [filtered, sortKey, sortDir]
  );

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'name' || key === 'homeFacility' || key === 'userType' ? 'asc' : 'desc');
    }
  };

  const companies = useMemo(() => {
    const m = new Map();
    for (const p of reps) {
      const co = p.homeFacility || '(unknown company)';
      m.set(co, (m.get(co) || 0) + 1);
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]).slice(0, 12);
  }, [reps]);

  const repsWithCollab = reps.filter(p => p.collaboratingFacilities.length > 0).length;

  return (
    <div className="users-tab-panel">
      <p className="users-tab-intro">
        Medical device / vendor reps identified by <code>user_type</code> in cached login data.
        Company = the user&apos;s home facility on their login record. Collaborating facilities
        are other sites seen in login activity, messages, or referrals (when present in cache).
        Window: <strong>{yearLabel}</strong>.
      </p>

      <div className="users-kpi-row">
        <div className="users-kpi"><span className="users-kpi-val">{reps.length.toLocaleString()}</span><span className="users-kpi-label">Reps with logins</span></div>
        <div className="users-kpi"><span className="users-kpi-val">{reps.reduce((s, p) => s + p.logins, 0).toLocaleString()}</span><span className="users-kpi-label">Total logins</span></div>
        <div className="users-kpi"><span className="users-kpi-val">{repsWithCollab.toLocaleString()}</span><span className="users-kpi-label">With collab sites</span></div>
      </div>

      {companies.length > 0 && (
        <div className="users-company-chips">
          <span className="users-company-label">Top companies:</span>
          {companies.map(([name, count]) => (
            <span key={name} className="users-company-chip" title={`${count} reps`}>{name} ({count})</span>
          ))}
        </div>
      )}

      <div className="users-tab-toolbar">
        <input
          type="search"
          className="users-search"
          placeholder="Search rep, company, facility…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label className="users-sort">
          Override type filter
          <input
            type="text"
            placeholder="e.g. industry rep"
            value={customTypeFilter}
            onChange={(e) => setCustomTypeFilter(e.target.value)}
          />
        </label>
      </div>

      {filtered.length === 0 ? (
        <p className="users-empty">
          No device reps matched. Try widening the date range, clearing the type override,
          or check Raw Data → Logins for actual <code>user_type</code> values in your cache.
        </p>
      ) : (
        <div className="users-table-wrap">
          <table className="users-table">
            <thead>
              <tr>
                <SortableTh label="Rep" sortKey="name" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                <SortableTh label="Type" sortKey="userType" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                <SortableTh label="Company (home facility)" sortKey="homeFacility" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                <SortableTh label="Logins" sortKey="logins" activeKey={sortKey} dir={sortDir} onSort={toggleSort} className="num" />
                <SortableTh label="Days" sortKey="loginDays" activeKey={sortKey} dir={sortDir} onSort={toggleSort} className="num" />
                <SortableTh label="Messages" sortKey="messagesSent" activeKey={sortKey} dir={sortDir} onSort={toggleSort} className="num" />
                <SortableTh label="Collab sites" sortKey="collaboratingCount" activeKey={sortKey} dir={sortDir} onSort={toggleSort} className="num" />
                <SortableTh label="Collaborating facilities" sortKey="collaboratingFacilities" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              </tr>
            </thead>
            <tbody>
              {sorted.map(p => (
                <tr key={p.userId}>
                  <td><PersonContactHover userId={Number(p.userId)}>{p.name || `User ${p.userId}`}</PersonContactHover></td>
                  <td>{p.userType || '—'}</td>
                  <td title={p.homeFacility}>{p.homeFacility || '—'}</td>
                  <td className="num">{p.logins.toLocaleString()}</td>
                  <td className="num">{p.loginDays}</td>
                  <td className="num">{p.messagesSent.toLocaleString()}</td>
                  <td className="num">{p.collaboratingCount}</td>
                  <td className="users-collab-cell">
                    {p.collaboratingFacilities.length === 0
                      ? '—'
                      : p.collaboratingFacilities.slice(0, 5).join(' · ')
                        + (p.collaboratingFacilities.length > 5 ? ` (+${p.collaboratingFacilities.length - 5} more)` : '')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default DeviceRepsTab;
